import classNames from "classnames";
import { Entry } from "contentful";
import { useMemo } from "react";

import Container from "~/components/common/container";
import EscapeNewLine from "~/components/common/escape-new-line";
import Grid from "~/components/common/grid";
import HighlightedText from "~/components/common/highlighted-text";
import Editorials from "~/types/editorials";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType } from "~/utils/editorial-utils";

import EditorialLink from "../editorial-link/editorial-link";
import LoyaltyBlocks from "./blocks/loyalty-blocks";
import styles from "./editorial-loyalty.module.scss";
import LoyaltySteps from "./steps/loyalty-steps";
import LoyaltyTabs from "./tabs/loyalty-tabs";

type Props = {
  entry: Entry<unknown>;
};

export default function EditorialLoyalty(props: Props) {
  assertEditorialType<Editorials.Loyalty>(props.entry, "loyalty");

  const { backgroundColor, withoutContainer } = props.entry.fields;

  return (
    <div className={classNames(styles.background, backgroundColor ? styles[backgroundColor] : undefined)}>
      {withoutContainer ? (
        <Grid>
          <div className={styles.containerFullWidth}>
            <LoyaltyContent entry={props.entry} />
          </div>
        </Grid>
      ) : (
        <Container>
          <Grid>
            <div className={styles.container}>
              <LoyaltyContent entry={props.entry} />
            </div>
          </Grid>
        </Container>
      )}
    </div>
  );
}

function LoyaltyContent(props: { entry: Entry<Editorials.Loyalty> }) {
  const { title, description, loyaltyStructure, link, backgroundColor } = props.entry.fields;
  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);

  const content = useMemo(() => {
    if (!loyaltyStructure) {
      return null;
    }

    const loyaltyModel = loyaltyStructure.model;
    const isBackgroundGradient = backgroundColor === "gradient";

    switch (loyaltyStructure.type) {
      case "tabs":
        return (
          <LoyaltyTabs
            tabs={loyaltyModel as Editorials.LoyaltyTabsModel[]}
            isBackgroundGradient={isBackgroundGradient}
          />
        );
      case "blocks":
        return (
          <LoyaltyBlocks
            blocks={loyaltyModel as Editorials.LoyaltyBlocksModel[]}
            isBackgroundGradient={isBackgroundGradient}
          />
        );
      case "steps":
        return (
          <LoyaltySteps
            steps={loyaltyModel as Editorials.LoyaltyStepsModel[]}
            isBackgroundGradient={isBackgroundGradient}
          />
        );
      default:
        return null;
    }
  }, [backgroundColor, loyaltyStructure]);

  return (
    <>
      {title ? (
        <h2 {...inspectorMode?.getProps("title")} className={styles.title}>
          <HighlightedText text={title} />
        </h2>
      ) : null}
      {description ? (
        <p {...inspectorMode?.getProps("description")} className={styles.description}>
          <EscapeNewLine text={description} />
        </p>
      ) : null}
      {content ? (
        <div
          {...inspectorMode?.getProps("loyaltyStructure")}
          className={title || description ? styles.wrapperStructure : undefined}
        >
          {content}
        </div>
      ) : null}
      {link ? (
        <div className={styles.wrapperLink}>
          <EditorialLink entry={link} lookAsButton="primary" />
        </div>
      ) : null}
    </>
  );
}
